/**
 * Cleanup utilities for floor plan elements
 * Removes base64 photo data from window/door/radiator elements
 */

import { getSize, formatBytes } from './floorPlanSizeAnalysis';

export interface CleanupResult {
  elementsProcessed: number;
  elementsWithPhotos: number;
  elementsWithBase64: number;
  sizeBeforeCleanup: number;
  sizeAfterCleanup: number;
  savedBytes: number;
  cleanedElements: any[];
}

/**
 * Clean base64 photos from floor plan elements
 * Photos should not be stored in floor plan elements - they belong in room_details
 */
export function cleanupFloorPlanElements(elements: any[]): CleanupResult {
  if (!Array.isArray(elements)) {
    return {
      elementsProcessed: 0,
      elementsWithPhotos: 0,
      elementsWithBase64: 0,
      sizeBeforeCleanup: 0,
      sizeAfterCleanup: 0,
      savedBytes: 0,
      cleanedElements: []
    };
  }

  const sizeBeforeCleanup = getSize(elements);
  let elementsWithPhotos = 0;
  let elementsWithBase64 = 0;

  // Clean elements - remove photos field from windows/doors/radiators
  const cleanedElements = elements.map(element => {
    if (!element || !element.photos) {
      return element;
    }

    elementsWithPhotos++;
    
    // Check if photos contain base64
    const photosString = JSON.stringify(element.photos);
    if (photosString.includes('data:image') || photosString.includes('base64')) {
      elementsWithBase64++;
      console.log(`🧹 Removing photos from ${element.type} element: ${element.id}`);
      console.log(`   Size: ${formatBytes(getSize(element.photos))}`);
    }

    // Create cleaned element WITHOUT photos
    const { photos, ...cleanedElement } = element;
    return cleanedElement;
  });

  const sizeAfterCleanup = getSize(cleanedElements);
  const savedBytes = sizeBeforeCleanup - sizeAfterCleanup;

  return {
    elementsProcessed: elements.length,
    elementsWithPhotos,
    elementsWithBase64,
    sizeBeforeCleanup,
    sizeAfterCleanup,
    savedBytes,
    cleanedElements
  };
}

/**
 * Log cleanup results
 */
export function logCleanupResults(result: CleanupResult): void {
  console.log('');
  console.log('🧹 ========================================');
  console.log('🧹 FLOOR PLAN ELEMENTS CLEANUP RESULTS');
  console.log('🧹 ========================================');
  console.log(`🧹 Elements processed: ${result.elementsProcessed}`);
  console.log(`🧹 Elements with photos: ${result.elementsWithPhotos}`);
  console.log(`🧹 Elements with base64: ${result.elementsWithBase64}`);
  console.log('🧹 ========================================');
  console.log(`📊 Size before: ${formatBytes(result.sizeBeforeCleanup)}`);
  console.log(`📊 Size after: ${formatBytes(result.sizeAfterCleanup)}`);
  console.log(`💾 Saved: ${formatBytes(result.savedBytes)} (${((result.savedBytes / result.sizeBeforeCleanup) * 100).toFixed(1)}%)`);
  console.log('🧹 ========================================');
  
  if (result.savedBytes > 1024 * 1024) {
    console.log('✅ Successfully cleaned floor plan elements!');
    console.log('💡 Photos should be stored in room_details, not floor plan elements');
  } else if (result.elementsWithPhotos === 0) {
    console.log('✅ No photos found in elements (optimal)');
  } else {
    console.log('⚠️ Some photos found but already on CDN');
  }
  
  console.log('🧹 ========================================');
  console.log('');
}

/**
 * Expose cleanup functions to window for manual testing
 */
export function exposeFloorPlanCleanup(surveyData: any, onUpdate: (data: any) => void): void {
  (window as any).cleanupFloorPlanElements = () => {
    const elements = surveyData?.floor_plan?.linkedFloorPlanData?.elements;
    if (!Array.isArray(elements)) {
      console.log('❌ No elements found in floor plan data');
      return;
    }

    console.log('🧹 Starting floor plan elements cleanup...\n');
    
    const result = cleanupFloorPlanElements(elements);
    logCleanupResults(result);

    if (result.savedBytes > 0) {
      console.log('💾 Saving cleaned floor plan data...');
      
      // Update floor plan data with cleaned elements
      const updatedFloorPlan = {
        ...surveyData.floor_plan,
        linkedFloorPlanData: {
          ...surveyData.floor_plan.linkedFloorPlanData,
          elements: result.cleanedElements
        }
      };

      // Call update function
      onUpdate({
        ...surveyData,
        floor_plan: updatedFloorPlan
      });

      console.log('✅ Floor plan data updated and saved!');
    }

    return result;
  };
}
